import React, { FC, forwardRef, Ref, useState } from 'react';
import styled from 'styled-components';

import OnOffSwitch from '@rover/components/src/buttons/OnOffSwitch/OnOffSwitch';
import Popover, { PopoverPlacement } from '@rover/components/src/callouts/Popover';
import { PopoverEvent } from '@rover/components/src/callouts/Popover/popover.constants';
import AlertInfo from '@rover/icons/svg/alert-info.svg';
import { Box, Button, Flex, Heading, Paragraph } from '@rover/kibble/core';
import Modal from '@rover/react-lib/src/components/modals/Modal';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';

type ModalProps = {
  extraInfoContent: string;
  extraInfoTitle: string;
};

type PopoverProps = {
  extraInfoContent: string;
  popoverPlacement?: PopoverPlacement;
  popoverEvent?: PopoverEvent;
};

type ExtraInfoProps = ModalProps &
  PopoverProps & {
    onExtraInfoPress?: () => void;
    extraInfoTriggerRef?: Ref<HTMLElement>;
  };

type SwitchLabelProps = { textContent: string; bigLabel?: boolean; hasExtraInfo?: boolean };

export type SwitchProps = Partial<ExtraInfoProps> & {
  extraInfoContent?: string;
  textContent: string;
  onChange: (frequency: boolean) => void;
  on: boolean;
  bigLabel?: boolean;
};

// The popover content inherits label style without it.
const StyledPopover = styled(Popover)`
  font-weight: normal;
`;

export const PopoverAlertInfo: FC<PopoverProps> = ({
  extraInfoContent,
  popoverPlacement,
  popoverEvent,
}) => {
  const event = popoverEvent || PopoverEvent.HOVER;
  const placement = popoverPlacement || PopoverPlacement.BOTTOM;
  return (
    <Box mr="4x" ml="2x" flex="0 0 auto">
      <StyledPopover
        content={extraInfoContent}
        event={event}
        placement={placement}
        maxWidth="240px"
        display="block"
      >
        {/* The margin-top is necessary to counter the effect of popover wrapper. */}
        <AlertInfo width={14} height={14} style={{ marginTop: '6px' }} />
      </StyledPopover>
    </Box>
  );
};

const StyledModal = styled(Modal)`
  &.RoverModalContent {
    height: fit-content !important;
    padding: ${({ theme }) => theme.space['6x']} !important;
    border-radius: ${({ theme }) => theme.radii.secondary};
  }
  padding-top: 50%;
  padding-left: ${({ theme }) => theme.space['6x']};
  padding-right: ${({ theme }) => theme.space['6x']};
`;

const AlertInfoIcon = (): JSX.Element => <AlertInfo width={16} height={16} />;

type AlertInfoTriggerProps = { onPress: () => void; label: string };

const AlertInfoTrigger = forwardRef<HTMLElement, AlertInfoTriggerProps>(
  ({ onPress, label }, ref) => (
    <Box ml="2x" flex="none">
      <Button
        variant="flat"
        size="small"
        icon={AlertInfoIcon}
        onPress={onPress}
        testID="alert-info-icon"
        p="1x"
        webProps={{ 'aria-label': label, ref }}
      />
    </Box>
  )
);

AlertInfoTrigger.displayName = 'AlertInfoTrigger';

export const ModalAlertInfo: FC<ModalProps> = ({ extraInfoContent, extraInfoTitle }) => {
  const [isOpen, setIsOpen] = useState(false);

  const openModal = (): void => setIsOpen(true);
  const closeModal = (): void => setIsOpen(false);

  return (
    <>
      <AlertInfoTrigger onPress={openModal} label={extraInfoTitle} />
      <StyledModal isOpen={isOpen} onRequestClose={closeModal} hasCloseButton withBorderRadius>
        <Heading size="300" testID="modal-alert-info-heading">
          {extraInfoTitle}
        </Heading>
        <Paragraph size="200">{extraInfoContent}</Paragraph>
      </StyledModal>
    </>
  );
};

export const ExtraInfo: FC<ExtraInfoProps> = ({
  extraInfoContent,
  extraInfoTitle,
  popoverEvent,
  popoverPlacement,
  onExtraInfoPress,
  extraInfoTriggerRef,
}) => {
  /* Additional information will show as a popover on desktop (triggered on hover by default) and modal on mobile
     (triggered on click). */
  if (isMobileBrowser()) {
    if (onExtraInfoPress) {
      return (
        <AlertInfoTrigger
          onPress={onExtraInfoPress}
          label={extraInfoTitle}
          ref={extraInfoTriggerRef}
        />
      );
    }

    return <ModalAlertInfo extraInfoContent={extraInfoContent} extraInfoTitle={extraInfoTitle} />;
  }

  return (
    <PopoverAlertInfo
      extraInfoContent={extraInfoContent}
      popoverEvent={popoverEvent}
      popoverPlacement={popoverPlacement}
    />
  );
};

const SwitchLabel: FC<SwitchLabelProps> = ({ textContent, bigLabel, hasExtraInfo }) => (
  <Box maxWidth={hasExtraInfo ? ['190px', 'none'] : 'none'} minWidth="0">
    <Heading
      size={bigLabel ? '200' : '100'}
      webProps={{ as: 'span' }}
      textColor="primary"
      fontWeight="semibold"
      mb="0x"
    >
      {textContent}
    </Heading>
  </Box>
);

const SwitchWithText: FC<SwitchProps> = ({
  textContent,
  onChange,
  extraInfoContent,
  extraInfoTitle,
  onExtraInfoPress,
  extraInfoTriggerRef,
  popoverEvent,
  popoverPlacement,
  on,
  bigLabel,
}) => {
  const extraInfo =
    extraInfoContent && extraInfoTitle ? (
      <ExtraInfo
        popoverEvent={popoverEvent}
        popoverPlacement={popoverPlacement}
        extraInfoContent={extraInfoContent}
        extraInfoTitle={extraInfoTitle}
        onExtraInfoPress={onExtraInfoPress}
        extraInfoTriggerRef={extraInfoTriggerRef}
      />
    ) : undefined;

  return (
    <Flex justifyContent="space-between">
      <OnOffSwitch
        disabled={false}
        on={on}
        onToggle={(value): void => onChange(value)}
        label={
          <SwitchLabel textContent={textContent} bigLabel={bigLabel} hasExtraInfo={!!extraInfo} />
        }
        labelSuffix={extraInfo}
        hideLabel={false}
        inlineLabel
      />
    </Flex>
  );
};

export default SwitchWithText;
